import axios from 'axios';
import config from '../config';

const { remoteUrl, remoteApiKey } = config || {};

const REQUEST_TIMEOUT_MS = 15000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

const requestHeaders = {
  'Content-Type': 'application/json',
  'x-api-key': remoteApiKey,
};

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const isRetryable = (error) => {
  const status = error?.response?.status;
  if (error?.code === 'ECONNABORTED') return true;
  if (!error?.response) return true;
  return status >= 500 || status === 429;
};

const getErrorMessage = (label, error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.message || error?.message || 'Unknown error';
  return status ? `${label} (${status}): ${detail}` : `${label}: ${detail}`;
};

const requestWithRetry = async (requestFn, label) => {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await requestFn();
      return response.data;
    } catch (error) {
      lastError = error;
      if (attempt < MAX_RETRIES && isRetryable(error)) {
        // eslint-disable-next-line no-await-in-loop
        await delay(RETRY_DELAY_MS * (attempt + 1));
      } else {
        break;
      }
    }
  }
  throw new Error(getErrorMessage(label, lastError));
};

const processDropdownGroups = (dropdownGroups) => ({
  devices: dropdownGroups.map((group) => ({
    thing_name: group.device,
    sensors: {
      Polar: group.sensors.includes('Polar'),
      Vaisala: group.sensors.includes('Vaisala'),
      Kurz: group.sensors.includes('Kurz'),
    },
    ev_num: group.ev,
    ...(group.suitPressure ? { suit_pressure_psid: Number(group.suitPressure) } : {}),
  })),
});

export const fetchDeviceOptions = async () => requestWithRetry(
  () => axios.get(`${remoteUrl}/trials`, {
    headers: requestHeaders,
    timeout: REQUEST_TIMEOUT_MS,
  }),
  'error fetching device options',
);

export const startDataCollection = async (dropdownGroups) => {
  const processedData = processDropdownGroups(dropdownGroups);
  return requestWithRetry(
    () => axios.post(`${remoteUrl}/trials`, processedData, {
      headers: requestHeaders,
      timeout: REQUEST_TIMEOUT_MS,
    }),
    'error starting data collection',
  );
};

export const stopDataCollection = async (activeDevices, trialId) => {
  const processedData = {
    trial_id: trialId,
    devices: activeDevices.map((device) => device.thing_name),
  };
  return requestWithRetry(
    () => axios.put(`${remoteUrl}/trials`, processedData, {
      headers: requestHeaders,
      timeout: REQUEST_TIMEOUT_MS,
    }),
    'error stopping data collection',
  );
};

export default startDataCollection;
